import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel,
  Heading, HStack, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Table, Tbody,
  Td, Text, Th, Thead, Tr, Tooltip, useDisclosure, VStack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiUsers } from 'react-icons/fi';

export default function AdminUsers({ token, apiBase }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ username: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const headers = { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${apiBase}/api/v1/admin-users/`, { headers })
      .then(r => r.json())
      .then(data => setUsers(data.results || []))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [apiBase, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    setFormError('');
    try {
      const res = await fetch(`${apiBase}/api/v1/admin-users/`, {
        method: 'POST', headers, body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || 'Failed to create user'); return; }
      onClose();
      load();
    } catch {
      setFormError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${apiBase}/api/v1/admin-users/${id}/`, { method: 'DELETE', headers });
    load();
  };

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Admin Users</Heading>
          <Text color="gray.500" mt={1}>Manage admin panel access</Text>
        </Box>
        <Button leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={onOpen}>
          Add User
        </Button>
      </Flex>

      {error && <Alert status="error" mb={4}><AlertIcon />{error}</Alert>}
      {loading && <Spinner size="xl" color="blue.500" />}

      {!loading && users.length > 0 && (
        <Box bg="white" rounded="xl" shadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Username</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map(u => (
                <Tr key={u.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="semibold">{u.username}</Td>
                  <Td color="gray.500">{u.email || '—'}</Td>
                  <Td>
                    <Badge colorScheme={u.is_superuser ? 'purple' : 'blue'}>
                      {u.is_superuser ? 'Superuser' : 'Staff'}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={u.is_active ? 'green' : 'gray'}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <Tooltip label="Delete">
                      <IconButton
                        icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red"
                        onClick={() => handleDelete(u.id)} aria-label="Delete"
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Admin User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {formError && <Alert status="error" mb={4} rounded="lg"><AlertIcon />{formError}</Alert>}
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  value={formData.username}
                  onChange={e => setFormData(f => ({ ...f, username: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(f => ({ ...f, email: e.target.value }))}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData(f => ({ ...f, password: e.target.value }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={handleCreate}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
